import { Injectable, InjectionToken, inject } from '@angular/core';
import * as api from '@opentelemetry/api';
import type { Attributes, Span, SpanOptions as OtelSpanOptions } from '@opentelemetry/api';
import { MonoTypeOperatorFunction, Observable, tap } from 'rxjs';

export type SpanOptions = Pick<OtelSpanOptions, 'startTime' | 'attributes'>;

export const TRACING_ONPREM_LEGACY = new InjectionToken<{
    startSpan: (operationName: string, options?: SpanOptions, identifier?: string) => Span;
    finishSpan: (span: Span | string, finishTime?: number) => void;
    getSpan: (identifier: string) => Span | undefined;
}>('TRACING_ONPREM_LEGACY');

@Injectable({ providedIn: 'root' })
export class TracingService {
    #internalTracingService = inject(TRACING_ONPREM_LEGACY, { optional: true });

    #activeSpans = new Map<string, Span>();

    /**
     * Starts a new span.
     * Works on both on-premise and cloud versions.
     *
     * NOTE: For cloud SPA or libraries with only cloud support,
     * there is no advantage to using this method; you should use the OpenTelemetry API directly.
     *
     * @example
     * import * as api from '@opentelemetry/api';
     * api.trace.getTracer('MyService').startSpan(operationName, options);
     *
     * @param operationName Operation name to use for span, will show up in AMA/spoud or in Azure application insights.
     * @param options optional, to set a startTime or attributes.
     * @param identifier optional, only for internal usage.
     */
    startSpan(operationName: string, options: SpanOptions = {}, identifier?: string): Span {
        // only for legacy on prem support
        if (this.#internalTracingService) {
            return this.#internalTracingService.startSpan(operationName, options, identifier);
        }

        // This is the core OpenTelemetry call; you can (and probably should if not on-prem) use it directly instead of this method.
        const span = api.trace.getTracer('RwcUtilsTracingService').startSpan(operationName, options);

        this.#activeSpans.set(operationName, span);
        if (identifier) {
            this.#activeSpans.set(identifier, span);
        }
        return span;
    }

    /**
     * Finishing a span will put it in a buffer and eventually send it to the backend.
     * @param span can be the real span object or a string which represent spanIdentifier or operationName
     * @param finishTime if not given, the current time will be used
     */
    finishSpan(span: Span | string | undefined, finishTime?: number): void {
        if (!span) {
            return;
        }
        // only for legacy on prem support
        if (this.#internalTracingService) {
            return this.#internalTracingService.finishSpan(span, finishTime);
        }

        (typeof span === 'string' ? this.getSpan(span) : span)?.end(finishTime);
        this.#activeSpans.forEach((value, key) => {
            if (!value.isRecording()) {
                this.#activeSpans.delete(key);
            }
        });
    }

    getSpan(identifier: string): Span | undefined {
        if (this.#internalTracingService) {
            return this.#internalTracingService.getSpan(identifier);
        }
        return this.#activeSpans.get(identifier);
    }

    /**
     *
     * @param operationName Operation name to use for span, will show up in AMA/spoud
     * @param options optional, to set parameter like startTime or tags. childOf shouldn't be used,
     */
    traceObservable<T>(operationName: string, options: SpanOptions = {}): MonoTypeOperatorFunction<T> {
        return this.#createOperatorFunction(this.startSpan(operationName, options));
    }

    /**
     * Create a span with a duration from the start of the request until now
     * @param operationName Operation name to use for span, will show up in AMA/spoud
     * @param attributes Optional tags for AMA/spoud
     */
    recordBootstrapTimeNow(operationName: string, attributes: Attributes = {}): void {
        const startTime = performance?.timeOrigin;
        const span = this.startSpan(operationName, { startTime, attributes });
        this.finishSpan(span);
    }

    #createOperatorFunction<T>(span: Span) {
        return (input$: Observable<T>) =>
            input$.pipe(
                tap({
                    next: () => this.finishSpan(span),
                    complete: () => this.finishSpan(span),
                    error: () => this.finishSpan(span)
                })
            );
    }
}
